import { useCallback, useEffect } from 'react';
import { logger } from '../../common/logger';
import { isClient } from '../../common/utils';

export const setupStorage = <Type>(key: string, initialValue: Type) => {
  try {
    if (!isClient()) {
      return initialValue;
    }
    const item = window.localStorage.getItem(key);
    if (item) {
      return JSON.parse(item) as Type;
    }

    return initialValue;
  } catch (err) {
    logger.error({ err }, `Failed to setup localStorage with key: ${key}`);
    return initialValue;
  }
};

/**
 * Hook for using localStorage with a key.
 * @param key - The key to use in localstorage.
 * @param initialValue - The initial value to put in the given key in localstorage when initialized.
 * @param setItemCallback - Callback function to call when setting an item. For backwards compatibility.
 * @returns `getItem` and `setItem` funcions as well as the `eventKey` in the form of `storage:[key]` for listening to changes.
 */
export const useLocalStorage = <Type>({
  key,
  initialValue,
  setItemCallback,
}: {
  key: string;
  initialValue?: Type;
  setItemCallback?: (value: Type) => void;
}) => {
  const eventKey = `storage:${key}`;

  const getItem = useCallback(() => {
    try {
      if (isClient()) {
        const item = window.localStorage.getItem(key);
        if (item) {
          return JSON.parse(item) as Type;
        }
      }
    } catch (err) {
      logger.error({ err }, `Failed to get localStorage with key: ${key}`);
      return;
    }
  }, [key]);

  const setItem = useCallback(
    (value: Type) => {
      try {
        if (isClient()) {
          window.localStorage.setItem(key, JSON.stringify(value));

          window.dispatchEvent(new CustomEvent(eventKey));

          if (setItemCallback) {
            setItemCallback(value);
          }
        }
      } catch (err) {
        logger.error(
          { err },
          `Failed to set localStorage with key: ${eventKey}`
        );
      }
    },
    [key, eventKey, setItemCallback]
  );

  useEffect(() => {
    const storedValue = getItem();
    if (!storedValue && initialValue !== undefined) {
      setItem(initialValue);
    }
  }, [initialValue, setItem, getItem]);

  return { getItem, setItem, eventKey };
};
